/**
 * A machine readable description of every field in the ingest contract.
 *
 * Two consumers read this:
 *
 *  1. `codegen/to_python.ts`, which emits the dataclasses and the field level
 *     checks in services/collector/schema/contract.py from it.
 *  2. `test/spec.test.ts`, which asserts that this description and the Zod
 *     schemas in ./event.ts name exactly the same fields, in the same order,
 *     with the same optionality.
 *
 * That second test is the drift gate. Without it this file would be a second
 * hand written copy of the contract, which is the exact failure mode the whole
 * package exists to prevent.
 */
import { MAX_ANON_ID_CHARS, MAX_DISTINCT_ID_CHARS, MAX_EVENT_NAME_CHARS, MAX_SDK_NAME_CHARS, MAX_SDK_VERSION_CHARS, MIN_EVENT_NAME_CHARS, } from './limits.js';
/** How a field is validated, and therefore how it is emitted in Python. */
export const FIELD_KINDS = [
    /** Matched against WRITE_KEY_RE before any database call. */
    'write_key',
    /** ISO-8601 with an explicit timezone. */
    'iso8601',
    /** The `{n, v}` SDK identity object. */
    'sdk',
    /** The events array, left raw here because each entry degrades separately. */
    'events',
    /** Crockford base32 ULID, normalised to upper case. */
    'ulid',
    /** Canonical UUID, any version. */
    'uuid',
    /** Bounded, non empty, no codepoint below 0x20. Enters a DynamoDB sort key. */
    'sort_key_string',
    /** Bounded string with no charset restriction. */
    'string',
    /** Non negative integer. */
    'int',
    /** A `props` or `properties` bag. */
    'properties',
];
/**
 * Values that are never a person.
 *
 * The union of the lists Amplitude, PostHog and Heap publish. Compared after
 * `.strip().lower()`, so `" NULL "` is caught too. The all zero UUID is here
 * because it satisfies the UUID shape and is the canonical "no id" sentinel.
 *
 * The reason for a blocklist at all, in Heap's words about a bad merge: it
 * "cannot be undone". One tenant calling `identify('undefined')` once binds
 * every anonymous browser in their project to a single person, in an append
 * only row, permanently.
 */
export const PLACEHOLDER_IDS = [
    '',
    'null',
    'undefined',
    'none',
    'nil',
    'nan',
    'true',
    'false',
    '0',
    '-1',
    'n/a',
    'na',
    'unknown',
    'anonymous',
    'anon',
    'guest',
    'not_authenticated',
    'distinct_id',
    'distinctid',
    'user_id',
    'userid',
    'id',
    'email',
    '[object object]',
    '00000000-0000-0000-0000-000000000000',
];
export const SDK_FIELDS = [
    {
        name: 'n',
        required: true,
        kind: 'string',
        minChars: 1,
        maxChars: MAX_SDK_NAME_CHARS,
        doc: 'SDK name, for example zelisight-js.',
    },
    {
        name: 'v',
        required: true,
        kind: 'string',
        minChars: 1,
        maxChars: MAX_SDK_VERSION_CHARS,
        doc: 'SDK version, for example 1.0.3.',
    },
];
export const EVENT_FIELDS = [
    {
        name: 'event_id',
        required: true,
        kind: 'ulid',
        doc: 'Client generated idempotency key, preserved across retries.',
    },
    {
        name: 'name',
        required: true,
        kind: 'sort_key_string',
        minChars: MIN_EVENT_NAME_CHARS,
        maxChars: MAX_EVENT_NAME_CHARS,
        doc: 'Event name. Enters a DynamoDB sort key, so it is charset guarded.',
    },
    {
        name: 'ts',
        required: true,
        kind: 'iso8601',
        doc: 'Client clock. Clamped to receive time per FR-ING-10, never rejected.',
    },
    {
        name: 'distinct_id',
        required: true,
        kind: 'sort_key_string',
        minChars: 1,
        maxChars: MAX_DISTINCT_ID_CHARS,
        doc: 'Person identifier. Enters a DynamoDB sort key.',
    },
    {
        name: 'anon_id',
        required: false,
        kind: 'uuid',
        maxChars: MAX_ANON_ID_CHARS,
        doc: 'Pre identify anonymous identifier.',
    },
    {
        name: 'session_id',
        required: false,
        kind: 'ulid',
        doc: 'Minted by the SDK. The server never re-segments a session.',
    },
    {
        name: 'url',
        required: false,
        kind: 'string',
        doc: 'Split server side into url_host and url_path.',
    },
    {
        name: 'retry',
        required: true,
        kind: 'int',
        doc: 'Client asserted retry count, therefore rate capped rather than trusted.',
    },
    {
        name: 'props',
        required: false,
        kind: 'properties',
        doc: 'Event properties. Degraded, never rejected.',
    },
];
export const BATCH_FIELDS = [
    {
        name: 'write_key',
        required: true,
        kind: 'write_key',
        doc: 'Matched by regex before any database call.',
    },
    {
        name: 'sent_at',
        required: true,
        kind: 'iso8601',
        doc: 'Used only for clock skew telemetry.',
    },
    { name: 'sdk', required: true, kind: 'sdk', doc: 'SDK identity.' },
    {
        name: 'events',
        required: true,
        kind: 'events',
        doc: 'One to MAX_EVENTS_PER_BATCH entries, each degraded separately.',
    },
];
export const IDENTIFY_FIELDS = [
    {
        name: 'write_key',
        required: true,
        kind: 'write_key',
        doc: 'Matched by regex before any database call.',
    },
    {
        name: 'anon_id',
        required: true,
        kind: 'uuid',
        maxChars: MAX_ANON_ID_CHARS,
        doc: 'The anonymous identifier being stitched.',
    },
    {
        name: 'distinct_id',
        required: true,
        kind: 'sort_key_string',
        minChars: 1,
        maxChars: MAX_DISTINCT_ID_CHARS,
        doc: 'The person identifier it is being bound to. Append only.',
    },
    {
        name: 'properties',
        required: false,
        kind: 'properties',
        doc: 'Person properties to set.',
    },
];
export const SCHEMA_SPECS = [
    {
        pythonName: 'Sdk',
        zodExport: 'SdkSchema',
        doc: 'SDK identity, so we can measure what is deployed in the wild.',
        fields: SDK_FIELDS,
    },
    {
        pythonName: 'Event',
        zodExport: 'EventSchema',
        doc: 'One captured event, after degradation.',
        fields: EVENT_FIELDS,
    },
    {
        pythonName: 'BatchEnvelope',
        zodExport: 'BatchSchema',
        doc: 'The POST /v1/e body, with its events left raw.',
        fields: BATCH_FIELDS,
    },
    {
        pythonName: 'IdentifyPayload',
        zodExport: 'IdentifySchema',
        doc: 'The POST /v1/i body.',
        fields: IDENTIFY_FIELDS,
        crossChecks: [
            {
                python: '_distinct_id == _anon_id',
                message: 'distinct_id must identify a person, not repeat the anon_id',
                doc: 'A browser cannot be bound to ITSELF. setPersonProperties() used to ' +
                    'post this for an anonymous visitor, and because a binding may only ' +
                    'ever be rewritten to the same value, which is what makes a real one ' +
                    'tamper proof, the self binding is PERMANENT: the real identify() ' +
                    'later gets a 409 and that browser can never be merged. 228 of the ' +
                    '358 bindings in the dev project were in that state.',
            },
            {
                python: '_distinct_id.strip().lower() in PLACEHOLDER_IDS',
                message: 'distinct_id is a placeholder value, not an identifier',
                doc: 'Placeholder ids, refused the way Amplitude, PostHog and Heap all ' +
                    'refuse them. A bad merge cannot be undone.',
            },
        ],
    },
];
//# sourceMappingURL=spec.js.map